import {
  type TerraUIMessage,
  type ToolCallGenericMessage,
  type HITLDecision,
  type HITLActionRequest,
  type HITLRequestConfirmationMessage,
  type HITLRequestConfirmationDecision,
  type BaseDataMessage,
  DataMessageTypeEnum,
  isTerminalToolName,
} from '@terra/shared-dto-chat-types';
import { isToolCallData } from './tool-call-data-merge';
import {
  findUserConfirmationForHITL,
  getHITLTargetIds,
  isDataOrConfirmationPart,
  isHITLRequestConfirmation,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';

export type HITLRequestIndexEntry = {
  messageIndex: number;
  partId: string | undefined;
  hitlMessage: HITLRequestConfirmationMessage;
  actionRequest: HITLActionRequest;
};

export type HITLRequestIndex = Map<string, HITLRequestIndexEntry[]>;

export function isBackgroundChildDataMessage(
  dataMessage: BaseDataMessage,
): boolean {
  const metadata = (dataMessage as { metadata?: { backgroundChild?: unknown } })
    .metadata;

  return metadata?.backgroundChild === true;
}

export function hitlRequestTargetsTerminalTool(
  dataMessage: HITLRequestConfirmationMessage,
): boolean {
  return (dataMessage.hitlRequestConfirmationPrompt?.actionRequests ?? []).some(
    (actionRequest) =>
      actionRequest.name ? isTerminalToolName(actionRequest.name) : false,
  );
}

export function hitlRequestActionToolCallIds(
  dataMessage: HITLRequestConfirmationMessage,
): string[] {
  return (dataMessage.hitlRequestConfirmationPrompt?.actionRequests ?? [])
    .map((actionRequest) => actionRequest.toolCallId ?? actionRequest.id)
    .filter((id): id is string => Boolean(id));
}

export function isResolvedToolCallResult(
  dataMessage: ToolCallGenericMessage,
): boolean {
  if (dataMessage.status === 'aborted') {
    return false;
  }

  if (
    dataMessage.type === DataMessageTypeEnum.ToolCallComplete ||
    dataMessage.type === DataMessageTypeEnum.ToolCallError
  ) {
    return true;
  }

  return (
    dataMessage.status === 'completed' ||
    dataMessage.status === 'error' ||
    dataMessage.status === 'result'
  );
}

export function collectSettledBackgroundTerminalToolCallIds(
  messages: TerraUIMessage[],
): Set<string> {
  const toolCallIds = new Set<string>();

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      if (!isDataOrConfirmationPart(part) || !part.data) {
        continue;
      }

      const dataMessage = parseBaseDataMessage(part.data);

      if (
        !dataMessage ||
        !isToolCallData(dataMessage) ||
        !isBackgroundChildDataMessage(dataMessage) ||
        !dataMessage.toolName ||
        !isTerminalToolName(dataMessage.toolName)
      ) {
        continue;
      }

      if (isResolvedToolCallResult(dataMessage)) {
        toolCallIds.add(dataMessage.toolCallId);
      }
    }
  }

  return toolCallIds;
}

function toolResultMatchesActionRequestByToolCallId({
  toolResult,
  actionRequest,
}: {
  toolResult: ToolCallGenericMessage;
  actionRequest: HITLActionRequest;
}): boolean {
  if (
    actionRequest.toolCallId &&
    toolResult.toolCallId === actionRequest.toolCallId
  ) {
    return true;
  }

  return Boolean(
    actionRequest.id && toolResult.toolCallId === actionRequest.id,
  );
}

function checkHasToolResult({
  messages,
  startIndex,
  actionRequests,
}: {
  messages: TerraUIMessage[];
  startIndex: number;
  actionRequests: HITLActionRequest[];
}): boolean {
  for (let i = startIndex; i < messages.length; i++) {
    const msg = messages[i];

    if (!msg?.parts) continue;

    for (const part of msg.parts) {
      if (!isDataOrConfirmationPart(part) || !part.data) continue;
      const dataMessage = parseBaseDataMessage(part.data);

      if (!dataMessage || !isToolCallData(dataMessage)) continue;

      if (
        isResolvedToolCallResult(dataMessage) &&
        actionRequests.some((actionRequest) =>
          toolResultMatchesActionRequestByToolCallId({
            toolResult: dataMessage,
            actionRequest,
          }),
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

export function resolveHITLDecision({
  dataMessage,
  messagePartId,
  messages,
  messageIndex,
}: {
  dataMessage: HITLRequestConfirmationMessage;
  messagePartId?: string;
  messages: TerraUIMessage[];
  messageIndex: number;
}): {
  hitlDecision: HITLRequestConfirmationDecision | undefined;
  hitlDecisions: HITLDecision[] | undefined;
  hasToolResult: boolean;
} {
  const hitlDecisionResult = findUserConfirmationForHITL({
    messages,
    startIndex: messageIndex + 1,
    targetMessageIds: getHITLTargetIds({ dataMessage, messagePartId }),
  });
  const embeddedDecision = dataMessage.decision;
  const hitlDecision = hitlDecisionResult.decision ?? embeddedDecision;
  const hitlDecisions =
    hitlDecisionResult.decisions ??
    (embeddedDecision ? [embeddedDecision] : undefined);

  if (!hitlDecision) {
    return {
      hitlDecision: undefined,
      hitlDecisions: undefined,
      hasToolResult: false,
    };
  }

  const actionRequests =
    dataMessage.hitlRequestConfirmationPrompt?.actionRequests ?? [];
  const hasToolResult = checkHasToolResult({
    messages,
    startIndex: messageIndex,
    actionRequests,
  });

  return { hitlDecision, hitlDecisions, hasToolResult };
}

export function indexHITLRequests(
  messages: TerraUIMessage[],
): HITLRequestIndex {
  const index: HITLRequestIndex = new Map();

  messages.forEach((message, messageIndex) => {
    for (const part of message.parts ?? []) {
      if (!isDataOrConfirmationPart(part) || !part.data) continue;
      const partData = parseBaseDataMessage(part.data);

      if (!partData || !isHITLRequestConfirmation(partData)) continue;

      for (const actionRequest of partData.hitlRequestConfirmationPrompt
        ?.actionRequests ?? []) {
        const keys = new Set(
          [actionRequest.toolCallId, actionRequest.id].filter(
            (key): key is string => Boolean(key),
          ),
        );

        for (const key of keys) {
          index.set(key, [
            ...(index.get(key) ?? []),
            {
              messageIndex,
              partId: part.id,
              hitlMessage: partData,
              actionRequest,
            },
          ]);
        }
      }
    }
  });

  return index;
}

function latestHITLRequestEntry({
  entries,
  messageIndex,
  isAwaitingExecution,
}: {
  entries: HITLRequestIndexEntry[] | undefined;
  messageIndex: number;
  isAwaitingExecution: boolean;
}): HITLRequestIndexEntry | undefined {
  const candidates = entries ?? [];
  const preceding = candidates.reduce<HITLRequestIndexEntry | undefined>(
    (chosen, entry) =>
      entry.messageIndex <= messageIndex &&
      (!chosen || entry.messageIndex > chosen.messageIndex)
        ? entry
        : chosen,
    undefined,
  );
  const following = isAwaitingExecution
    ? candidates.find((entry) => entry.messageIndex > messageIndex)
    : undefined;

  return (
    preceding ??
    candidates.find((entry) => Boolean(entry.hitlMessage.backgroundSubagent)) ??
    following
  );
}

export function resolveToolCallHITLStatus({
  dataMessage,
  messages,
  messageIndex,
  hitlIndex,
}: {
  dataMessage: ToolCallGenericMessage;
  messages: TerraUIMessage[];
  messageIndex: number;
  hitlIndex: HITLRequestIndex;
}): {
  wasShownInHITL: boolean;
  hitlResolvedToolResultInMessages: boolean;
  decision: HITLRequestConfirmationDecision | undefined;
} {
  const entry = dataMessage.toolName
    ? latestHITLRequestEntry({
        entries: hitlIndex.get(dataMessage.toolCallId),
        messageIndex,
        isAwaitingExecution:
          dataMessage.status === 'started' ||
          dataMessage.status === 'in-progress',
      })
    : undefined;

  if (!entry) {
    return {
      wasShownInHITL: false,
      hitlResolvedToolResultInMessages: false,
      decision: undefined,
    };
  }

  const userConfirmationDecision = findUserConfirmationForHITL({
    messages,
    startIndex: entry.messageIndex,
    targetMessageIds: getHITLTargetIds({
      dataMessage: entry.hitlMessage,
      messagePartId: entry.partId,
      actionRequests: [entry.actionRequest],
    }),
  }).decision;
  const resolvedDecision =
    entry.hitlMessage.decision ?? userConfirmationDecision;

  return {
    wasShownInHITL: true,
    hitlResolvedToolResultInMessages: resolvedDecision
      ? checkHasToolResult({
          messages,
          startIndex: entry.messageIndex,
          actionRequests: [entry.actionRequest],
        })
      : false,
    decision: resolvedDecision,
  };
}
